// Onglet Aperçu : prochaine audience, raccourcis chiffrés, description et fiche
// d'identité, à faire, à venir, derniers documents, activité.
// Parties/équipe/financier/échéances sont portés par le rail de synthèse (Task 6).
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SEMI_FRESH } from '../../lib/queryPresets';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  CheckCircle2, Pencil, X,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { JurisdictionSelect } from './JurisdictionSelect';
import { dossiersByJurisdictionHref, dossiersByTypeHref } from '../../lib/dossiers/dossierListLinks';
import { Input } from '../ui/Input';
import { cn } from '../../lib/cn';
import { useUpdateDossier } from '../../hooks/queries/useDossiers';
import { useDossierHearings } from '../../hooks/queries/useDossierHearings';
import { useDossierAppointments } from '../../hooks/queries/useAppointments';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { useTasks } from '../../hooks/queries/useTasks';
import {
  DOSSIER_TYPE_LABELS,
  listDossierActivity,
  type Dossier,
  type DossierType,
} from '../../services/dossiers/dossierService';
import { activityLabel, deadlineUrgency } from '../../services/dossiers/dossierListDerive';
import {
  aggregateDossierCalendarEvents,
  type DossierCalendarEventKind,
} from '../../services/calendar/dossierCalendar';
import { takeNextAgendaItems } from '../../services/calendar/agendaGroups';
import { useTaskModalStore } from '../../store/taskModalStore';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import { TaskCreateModal } from '../tasks/TaskCreateModal';
import { HearingModal } from './HearingModal';
import { useEffectiveDossierFields } from '../../hooks/queries/useCabinetSettings';
import { useTasksAssignments } from '../../hooks/queries/useTaskCollaboration';
import { TaskContextMenu, useTaskContextMenu } from '../tasks/TaskContextMenu';
import { useTasksStore } from '../../store/tasksStore';
import { useTaskCompletion } from '../../hooks/useTaskCompletion';
import { Avatar } from '../ui/Avatar';
import { DossierSummaryRail } from './DossierSummaryRail';
import { NextHearingBand, OverviewStats, RecentDocuments } from './DossierOverviewBlocks';

import { formatAppDate } from '../../lib/dateFormat';
interface Props {
  dossier: Dossier;
  canEdit: boolean;
}

const TYPES: DossierType[] = [
  'civil', 'penal', 'commercial', 'social',
  'immobilier', 'famille', 'admin', 'autre',
];

const AGENDA_KIND_LABEL: Record<DossierCalendarEventKind, string> = {
  hearing: 'Audience',
  task: 'Tâche',
  prescription: 'Prescription',
  appointment: 'Rendez-vous',
};

export function DossierOverviewTab({
  dossier,
  canEdit,
  canManageMembers,
}: Props & { canManageMembers: boolean }) {
  return (
    <div className="space-y-3.5">
      <NextHearingBand dossier={dossier} />
      <OverviewStats dossier={dossier} />
      {/* Le rail de synthèse démarre sous les quatre raccourcis, jamais à côté. */}
      <div className="flex items-start gap-5">
        <div className="min-w-0 flex-1 space-y-3.5">
          <DescriptionCard dossier={dossier} canEdit={canEdit} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
            <TodoCard dossierId={dossier.id} />
            <AgendaPreviewCard dossier={dossier} canEdit={canEdit} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
            <SectionShell title="Derniers documents" to={`/dossiers/${dossier.id}/documents`}>
              <RecentDocuments dossierId={dossier.id} />
            </SectionShell>
            <ActivityCard dossierId={dossier.id} />
          </div>
        </div>
        <div className="hidden xl:block">
          <DossierSummaryRail dossier={dossier} canManageMembers={canManageMembers} />
        </div>
      </div>
    </div>
  );
}

// ─── À faire : prochaines tâches ouvertes, puis les 2 dernières terminées ─────

function TodoCard({ dossierId }: { dossierId: string }) {
  const openTask = useTaskModalStore((s) => s.openTask);
  const canCreate = useCanAccessFeature('tasks_create');
  const canEdit = useCanAccessFeature('tasks_edit');
  // Un clic sur le cercle termine la tâche. Le message permet d'annuler un clic malheureux.
  const { finish, toggle, isPending: finishing } = useTaskCompletion();
  const [createOpen, setCreateOpen] = useState(false);
  const { data: tasks } = useTasks({ dossier_id: dossierId, completed: false });
  const pendingDeleteIds = useTasksStore((s) => s.pendingDeleteIds);
  const next3 = useMemo(
    () =>
      [...(tasks ?? [])]
        .filter((t) => !t.is_deleted && !pendingDeleteIds.has(t.id))
        .sort((a, b) => {
          const da = a.deadline ?? a.due_date;
          const db = b.deadline ?? b.due_date;
          if (!da && !db) return 0;
          if (!da) return 1;
          if (!db) return -1;
          return new Date(da).getTime() - new Date(db).getTime();
        })
        .slice(0, 4),
    [tasks, pendingDeleteIds],
  );
  // Les 2 dernières tâches terminées restent visibles sous les tâches ouvertes (demande du cabinet).
  const { data: doneTasks } = useTasks({ dossier_id: dossierId, completed: true });
  const lastDone = useMemo(
    () =>
      [...(doneTasks ?? [])]
        .filter((t) => !t.is_deleted && !pendingDeleteIds.has(t.id))
        .sort((a, b) => new Date(b.completed_at ?? 0).getTime() - new Date(a.completed_at ?? 0).getTime())
        .slice(0, 2),
    [doneTasks, pendingDeleteIds],
  );
  const taskIds = useMemo(() => [...next3, ...lastDone].map((t) => t.id), [next3, lastDone]);
  const { data: assignmentsMap } = useTasksAssignments(taskIds);
  const menu = useTaskContextMenu();

  return (
    <SectionShell
      title="À faire"
      to={`/dossiers/${dossierId}/taches`}
      action={
        canCreate ? (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            aria-label="Nouvelle tâche"
            title="Nouvelle tâche"
            className="p-1 -m-1 rounded opacity-50 hover:opacity-100 transition-opacity"
          >
            <img src="/icons/plus.svg" alt="" className="w-3.5 h-3.5" />
          </button>
        ) : undefined
      }
    >
      <TaskCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        dossierId={dossierId}
      />
      {next3.length === 0 && lastDone.length === 0 ? (
        <p className="text-xs text-neutral-400">Aucune tâche ouverte.</p>
      ) : (
        <div className="-mx-4 -my-4">
          {next3.length === 0 ? (
            <p className="border-b border-neutral-50 px-4 py-2.5 text-xs text-neutral-400">Aucune tâche ouverte.</p>
          ) : null}
          {next3.map((t) => {
            const date = t.deadline ?? t.due_date;
            const urgency = date ? deadlineUrgency(date) : null;
            const late = urgency === 'overdue';
            const owner = assignmentsMap?.get(t.id)?.[0];
            return (
              <div
                key={t.id}
                role="button"
                tabIndex={0}
                onClick={() => openTask(t.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target === e.currentTarget) openTask(t.id);
                }}
                onContextMenu={(e) => menu.open(e, t)}
                className={cn(
                  'flex cursor-pointer items-center gap-3 w-full border-b border-l-2 border-neutral-50 px-4 py-2.5 text-left transition-colors last:border-b-0 hover:bg-neutral-50',
                  late ? 'border-l-[#c8431f]' : 'border-l-transparent',
                )}
              >
                {canEdit ? (
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={false}
                    aria-label={`Terminer la tâche ${t.content}`}
                    title="Terminer la tâche"
                    disabled={finishing}
                    onClick={(e) => {
                      e.stopPropagation();
                      finish(t);
                    }}
                    className="group/ck grid h-[17px] w-[17px] flex-shrink-0 place-items-center rounded-full border-[1.5px] border-neutral-300 bg-white text-transparent transition-colors hover:border-[#2f7d5b] hover:text-[#2f7d5b]"
                  >
                    <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" aria-hidden>
                      <path d="M2.2 6.2 4.8 8.8 9.8 3.2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                ) : (
                  <span aria-hidden className="h-[17px] w-[17px] flex-shrink-0 rounded-full border-[1.5px] border-neutral-300" />
                )}
                <span className="flex-1 text-[13px] font-medium text-cime-text-primary truncate">{t.content}</span>
                {owner ? (
                  <Avatar name={owner.full_name} src={owner.avatar_url} size="xs" className="h-6 w-6 text-[10px]" />
                ) : null}
                {date && (
                  <span className={cn(
                    'min-w-[52px] text-right text-xs whitespace-nowrap',
                    urgency === 'urgent' || late ? 'text-[#c8431f] font-semibold' : 'text-neutral-400',
                  )}>
                    {formatAppDate(date, { year: false })}
                  </span>
                )}
              </div>
            );
          })}
          {lastDone.map((t) => {
            const owner = assignmentsMap?.get(t.id)?.[0];
            return (
              <div
                key={t.id}
                role="button"
                tabIndex={0}
                onClick={() => openTask(t.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target === e.currentTarget) openTask(t.id);
                }}
                onContextMenu={(e) => menu.open(e, t)}
                className="flex cursor-pointer items-center gap-3 w-full border-b border-l-2 border-neutral-50 border-l-transparent px-4 py-2.5 text-left transition-colors last:border-b-0 hover:bg-neutral-50"
              >
                {/* Pastille verte : tâche terminée. Un clic la rouvre. */}
                {canEdit ? (
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked
                    aria-label={`Rouvrir la tâche ${t.content}`}
                    title="Terminée · cliquer pour rouvrir"
                    disabled={finishing}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(t);
                    }}
                    className="grid h-[17px] w-[17px] flex-shrink-0 place-items-center rounded-full bg-[#2f7d5b] text-white transition-opacity hover:opacity-80"
                  >
                    <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" aria-hidden>
                      <path d="M2.2 6.2 4.8 8.8 9.8 3.2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                ) : (
                  <span aria-label="Terminée" className="grid h-[17px] w-[17px] flex-shrink-0 place-items-center rounded-full bg-[#2f7d5b] text-white">
                    <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" aria-hidden>
                      <path d="M2.2 6.2 4.8 8.8 9.8 3.2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                )}
                {/* Tâche terminée : texte grisé, jamais barré. */}
                <span className="flex-1 text-[13px] font-medium text-neutral-400 truncate">{t.content}</span>
                {owner ? (
                  <Avatar name={owner.full_name} src={owner.avatar_url} size="xs" className="h-6 w-6 text-[10px] opacity-70" />
                ) : null}
                {t.completed_at ? (
                  <span className="min-w-[52px] text-right text-xs whitespace-nowrap text-[#2f7d5b]" title="Terminée le">
                    {formatAppDate(t.completed_at, { year: false })}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      {menu.target ? (
        <TaskContextMenu target={menu.target} onClose={menu.close} onOpenTask={(task) => openTask(task.id)} />
      ) : null}
    </SectionShell>
  );
}

// ─── Dernière activité ────────────────────────────────────────────────────────

function ActivityCard({ dossierId }: { dossierId: string }) {
  const { data: entries } = useQuery({
    ...SEMI_FRESH,
    queryKey: ['dossier-activity', dossierId],
    queryFn: () => listDossierActivity(dossierId, 4),
  });
  const { data: collaborators } = useCollaborators();
  const actorName = (id: string | null) =>
    id ? collaborators?.find((c) => c.id === id)?.full_name ?? null : null;

  return (
    <SectionShell
      title="Activité"
      action={
        <Link to={`/dossiers/${dossierId}/timeline`} className="text-xs text-neutral-400 hover:text-cime-primary">
          Historique →
        </Link>
      }
    >
      {!entries?.length ? (
        <p className="text-xs text-neutral-400">Aucune activité enregistrée.</p>
      ) : (
        <div className="-mx-4 -my-4">
          {entries.map((e) => (
            <div key={e.id} className="px-4 py-2.5 border-b border-neutral-50 last:border-b-0 leading-tight">
              <span className="block text-[13px] font-medium text-cime-text-primary">
                {activityLabel(e.action)}
              </span>
              <span className="block text-[11px] text-neutral-400">
                {formatDistanceToNow(new Date(e.created_at), { addSuffix: true, locale: fr })}
                {actorName(e.actor_id) ? ` · ${actorName(e.actor_id)}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </SectionShell>
  );
}

// ─── Agenda : 3 prochaines échéances ─────────────────────────────────────────

function AgendaPreviewCard({ dossier, canEdit }: { dossier: Dossier; canEdit: boolean }) {
  const navigate = useNavigate();
  const [hearingOpen, setHearingOpen] = useState(false);
  const { prescription: showPrescription } = useEffectiveDossierFields();
  const openTask = useTaskModalStore((s) => s.openTask);
  const { data: tasks } = useTasks({ dossier_id: dossier.id, completed: false });
  const { data: hearings = [] } = useDossierHearings(dossier.id);
  const { data: appointments = [] } = useDossierAppointments(dossier.id);
  const next3 = useMemo(() => {
    const events = aggregateDossierCalendarEvents(
      {
        id: dossier.id,
        next_hearing_at: dossier.next_hearing_at,
        prescription_date: showPrescription ? dossier.prescription_date : null,
      },
      tasks ?? [],
      hearings,
      appointments,
    );
    return takeNextAgendaItems(events, new Date(), 3);
  }, [dossier.id, dossier.next_hearing_at, dossier.prescription_date, showPrescription, tasks, hearings, appointments]);

  return (
    <SectionShell
      title="À venir"
      to={`/dossiers/${dossier.id}/agenda`}
      action={
        canEdit ? (
          <button
            type="button"
            onClick={() => setHearingOpen(true)}
            aria-label="Ajouter une audience"
            title="Ajouter une audience"
            className="p-1 -m-1 rounded opacity-50 hover:opacity-100 transition-opacity"
          >
            <img src="/icons/plus.svg" alt="" className="w-3.5 h-3.5" />
          </button>
        ) : undefined
      }
    >
      <HearingModal
        open={hearingOpen}
        dossierId={dossier.id}
        hearing={null}
        onClose={() => setHearingOpen(false)}
      />
      {next3.length === 0 ? (
        <p className="text-xs text-neutral-400">Aucune échéance à venir.</p>
      ) : (
        <div className="-mx-4 -my-4">
          {next3.map((event) => {
            const d = new Date(event.start);
            return (
              <button
                key={event.id}
                type="button"
                onClick={() => {
                  if (event.kind === 'task' && event.taskId) {
                    openTask(event.taskId);
                    return;
                  }
                  if (event.kind === 'appointment' && event.appointmentId) {
                    navigate(`/dossiers/${dossier.id}/agenda?appointment=${event.appointmentId}`);
                    return;
                  }
                  navigate(`/dossiers/${dossier.id}/agenda`);
                }}
                className={cn(
                  'flex items-center gap-3 w-full border-b border-l-2 border-neutral-50 px-4 py-2 text-left transition-colors last:border-b-0 hover:bg-neutral-50',
                  event.kind === 'hearing' && 'border-l-[#ea7549]',
                  event.kind === 'task' && 'border-l-cime-primary',
                  event.kind === 'prescription' && 'border-l-red-600',
                  event.kind === 'appointment' && 'border-l-cime-gold',
                )}
              >
                <span className="w-9 flex-shrink-0 text-center leading-none">
                  <small className="block text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                    {format(d, 'EEE', { locale: fr })}
                  </small>
                  <b className="mt-0.5 block text-[17px] font-semibold tabular-nums text-cime-text-primary">
                    {format(d, 'd')}
                  </b>
                </span>
                <span className="min-w-0 flex-1 leading-tight">
                  <b className="block truncate text-[13px] font-semibold text-cime-text-primary">{event.title}</b>
                  <small className="block truncate text-xs text-neutral-400">
                    {[
                      event.allDay || !event.start.includes('T') ? null : format(d, 'HH:mm'),
                      AGENDA_KIND_LABEL[event.kind],
                      format(d, 'MMM yyyy', { locale: fr }),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </SectionShell>
  );
}

// ─── Description + fiche d'identité ──────────────────────────────────────────

function DescriptionCard({ dossier, canEdit }: Props) {
  const [editing, setEditing] = useState(false);
  const [description, setDescription] = useState(dossier.description ?? '');
  const [title, setTitle] = useState(dossier.title);
  const [type, setType] = useState<DossierType>(dossier.type_affaire);
  const [jurisdictionId, setJurisdictionId] = useState<string | null>(dossier.jurisdiction_id);
  const [chambre, setChambre] = useState(dossier.chambre ?? '');
  const [rgNumber, setRgNumber] = useState(dossier.rg_number ?? '');
  const [stake, setStake] = useState(stakeInput(dossier.amount_at_stake));
  const update = useUpdateDossier();
  const stakeValue = parseStake(stake);

  function reset() {
    setDescription(dossier.description ?? '');
    setTitle(dossier.title);
    setType(dossier.type_affaire);
    setJurisdictionId(dossier.jurisdiction_id);
    setChambre(dossier.chambre ?? '');
    setRgNumber(dossier.rg_number ?? '');
    setStake(stakeInput(dossier.amount_at_stake));
  }

  function save() {
    update.mutate(
      {
        id: dossier.id,
        patch: {
          description: description.trim() || null,
          title: title.trim(),
          type_affaire: type,
          jurisdiction_id: jurisdictionId,
          chambre: jurisdictionId ? chambre.trim() || null : null,
          rg_number: rgNumber.trim() || null,
          amount_at_stake: stakeValue ?? null,
        },
      },
      { onSuccess: () => setEditing(false) },
    );
  }

  return (
    <SectionShell
      title="Description"
      action={
        canEdit && !editing ? (
          <button
            onClick={() => {
              reset();
              setEditing(true);
            }}
            className="text-cime-text-muted hover:text-cime-primary"
            aria-label="Modifier"
            title="Modifier"
          >
            <Pencil className="w-4 h-4" />
          </button>
        ) : null
      }
    >
      {!editing ? (
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1">
            {dossier.description ? (
              <div className="max-h-[300px] overflow-y-auto">
                <p className="text-sm leading-relaxed text-cime-text-primary whitespace-pre-wrap">
                  {dossier.description}
                </p>
              </div>
            ) : (
              <p className="text-sm italic text-cime-text-muted">Aucune description.</p>
            )}
          </div>
          <dl className="w-full flex-shrink-0 rounded-[10px] border border-[#e6e2d8] bg-[#fbfaf6] px-3.5 py-1 lg:w-[280px]">
            <Fact
              label="Type d'affaire"
              value={
                <Link to={dossiersByTypeHref(dossier.type_affaire)} className={FACT_LINK} title="Voir tous les dossiers de ce type">
                  {DOSSIER_TYPE_LABELS[dossier.type_affaire]}
                </Link>
              }
            />
            <Fact
              label="Juridiction"
              value={
                dossier.juridiction ? (
                  <Link to={dossiersByJurisdictionHref(dossier.juridiction)} className={FACT_LINK} title="Voir tous les dossiers de cette juridiction">
                    {dossier.juridiction}
                  </Link>
                ) : (
                  '—'
                )
              }
            />
            {dossier.chambre ? <Fact label="Chambre" value={dossier.chambre} /> : null}
            <Fact label="N° RG" value={dossier.rg_number || '—'} />
            <Fact label="Ouvert le" value={formatAppDate(dossier.opened_at)} />
            {dossier.amount_at_stake != null ? (
              <Fact
                label="Enjeu"
                strong
                value={`${dossier.amount_at_stake.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €`}
              />
            ) : null}
          </dl>
        </div>
      ) : (
        <div className="space-y-3">
          <FieldEditable label="Intitulé">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </FieldEditable>
          <FieldEditable label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-cime focus:outline-none focus:ring-2 focus:ring-cime-primary/20 focus:border-cime-primary"
              placeholder="Contexte du dossier, faits, demandes…"
            />
          </FieldEditable>
          <FieldEditable label="Type d'affaire">
            <div className="grid grid-cols-4 gap-1.5">
              {TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={cn(
                    'px-2 py-1.5 text-xs rounded-md border transition',
                    type === t
                      ? 'bg-cime-primary text-white border-cime-primary'
                      : 'bg-white border-neutral-200 hover:border-cime-primary/40',
                  )}
                >
                  {DOSSIER_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </FieldEditable>
          <div className="grid grid-cols-2 gap-3">
            <FieldEditable label="Juridiction">
              <JurisdictionSelect value={jurisdictionId} onChange={(j) => setJurisdictionId(j?.id ?? null)} />
            </FieldEditable>
            <FieldEditable label="Chambre">
              <Input value={chambre} onChange={(e) => setChambre(e.target.value)} disabled={!jurisdictionId} placeholder="2e chambre civile…" />
            </FieldEditable>
            <FieldEditable label="N° RG">
              <Input value={rgNumber} onChange={(e) => setRgNumber(e.target.value)} />
            </FieldEditable>
            <FieldEditable label="Enjeu (€)">
              <Input
                inputMode="decimal"
                value={stake}
                onChange={(e) => setStake(e.target.value)}
                placeholder="Montant en litige"
              />
            </FieldEditable>
          </div>
          {stakeValue === undefined ? (
            <p className="text-xs text-[#c8431f]">L'enjeu doit être un montant positif, par exemple 186000.</p>
          ) : null}
          <FormActions
            onCancel={() => setEditing(false)}
            onSave={save}
            saving={update.isPending}
            canSave={title.trim().length > 0 && stakeValue !== undefined}
          />
        </div>
      )}
    </SectionShell>
  );
}

function stakeInput(value: number | null): string {
  return value == null ? '' : String(value).replace('.', ',');
}

/** Champ vide → null (pas d'enjeu). Saisie invalide → undefined. */
function parseStake(input: string): number | null | undefined {
  const text = input.replace(/[\s\u00a0\u202f€]/g, '').replace(',', '.');
  if (!text) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return undefined;
  return Number(text);
}

// ─── Helpers UI ──────────────────────────────────────────────────────────────

function SectionShell({
  title,
  to,
  action,
  children,
}: {
  title: string;
  /** Rend le titre cliquable (mène à l'onglet du bloc). */
  to?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white border border-neutral-200 rounded-cime overflow-hidden">
      <header className="flex items-center justify-between px-4 py-3 border-b border-cime-border bg-[#fbfaf7]">
        <h3 className="text-[11px] font-semibold tracking-[0.08em] uppercase text-cime-text-muted">
          {to ? (
            <Link to={to} className="hover:text-cime-primary transition-colors">
              {title}
            </Link>
          ) : (
            title
          )}
        </h3>
        {action}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

const FACT_LINK =
  'underline decoration-cime-primary/25 underline-offset-2 transition-colors hover:text-cime-primary hover:decoration-cime-primary';

function Fact({ label, value, strong }: { label: string; value: React.ReactNode; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[#e6e2d8] py-2 text-[13px] last:border-b-0">
      <dt className="text-neutral-400">{label}</dt>
      <dd className={cn('m-0 text-right font-medium text-cime-text-primary', strong && 'font-bold text-cime-primary')}>
        {value}
      </dd>
    </div>
  );
}

function FieldEditable({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function FormActions({
  onCancel,
  onSave,
  saving,
  canSave,
}: {
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  canSave: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-2 pt-1">
      <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
        <X className="w-4 h-4 mr-1" /> Annuler
      </Button>
      <Button size="sm" onClick={onSave} disabled={saving || !canSave}>
        <CheckCircle2 className="w-4 h-4 mr-1" />
        {saving ? 'Enregistrement…' : 'Enregistrer'}
      </Button>
    </div>
  );
}
